/**
 * @file order-ref-keys-field.tsx
 * @module app/modules/service-request/components/fields/order-ref-keys-field
 * @description "orderRefKeys" input kind: a list of order reference keys, edited as chips and
 * shown as chips with a "+N more" toggle.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import { useState } from 'react';
import RefKeyInput from './ref-key-input';
import { FieldDisplayProps, FieldEditorProps, FieldKind } from './field-kind';
import { MAX_REF_KEYS_PER_REQUEST } from '../../utils/service-request.constants';

const VISIBLE_CHIPS = 6;

const OrderRefKeysEditor = ({
  id,
  field,
  value,
  onChange,
  invalid,
  disabled,
  describedBy,
}: FieldEditorProps<string[]>) => (
  <RefKeyInput
    id={id}
    value={value}
    onChange={onChange}
    limit={field.maxItems ?? MAX_REF_KEYS_PER_REQUEST}
    invalid={invalid}
    disabled={disabled}
    describedBy={describedBy}
  />
);

const OrderRefKeysDisplay = ({ value }: FieldDisplayProps<string[]>) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? value : value.slice(0, VISIBLE_CHIPS);
  const hidden = value.length - visible.length;

  return (
    <div className="sr-chip-list">
      {visible.map((refKey) => (
        <span key={refKey} className="sr-chip sr-chip--static">
          {refKey}
        </span>
      ))}
      {hidden > 0 || expanded ? (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 sr-link"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'Show less' : `+${hidden} more`}
        </button>
      ) : null}
    </div>
  );
};

export const orderRefKeysKind: FieldKind<string[]> = {
  emptyValue: () => [],
  fromPayload: (raw) =>
    Array.isArray(raw)
      ? raw.filter((refKey): refKey is string => typeof refKey === 'string')
      : [],
  isEmpty: (value) => value.length === 0,
  validate: (value, field) => {
    const limit = field.maxItems ?? MAX_REF_KEYS_PER_REQUEST;
    return value.length > limit
      ? `Add at most ${limit} order reference keys.`
      : undefined;
  },
  Editor: OrderRefKeysEditor,
  Display: OrderRefKeysDisplay,
};
